import React, { useEffect, useState } from 'react';
import { Person, ContactType, Customer } from '../types';
import { customersAPI } from '../services/api';

interface PersonsTableProps {
  persons: Person[];
  onEdit: (person: Person) => void;
  onDelete: (id: number) => void;
  onViewDetails: (id: number) => void;
}

const PersonsTable: React.FC<PersonsTableProps> = ({
  persons,
  onEdit,
  onDelete,
  onViewDetails
}) => {
  const [customerGroupMap, setCustomerGroupMap] = useState<Record<number, string>>({});

  useEffect(() => {
    loadCustomerGroups();
  }, [persons]);

  const loadCustomerGroups = async () => {
    try {
      const customers = await customersAPI.getCustomers();
      const map = customers.reduce<Record<number, string>>((accumulator, customer: Customer) => {
        if (customer.group) {
          accumulator[customer.id] = customer.group;
        }
        return accumulator;
      }, {});
      setCustomerGroupMap(map);
    } catch (error) {
      console.error('Error loading customer groups:', error);
    }
  };
  // No truncation: show full customer names (use CSS wrapping / tooltip via browser)

  return (
    <div>
      <div className="table-responsive">
        <table className="table table-hover">
          <thead className="table-light">
            <tr>
              <th className="text-start">Name</th>
              <th className="text-start">Customer</th>
              <th className="text-start">Email</th>
              <th className="text-start">Phone</th>
              <th className="text-start">Actions</th>
            </tr>
          </thead>
        <tbody>
          {persons.map((person) => (
            <tr key={person.id}>
              <td>
                <div className="d-flex flex-column">
                  <span className="fw-bold">
                    {person.lastName} {person.firstName}
                    {person.contactType === ContactType.Hot && ' 🔥'}
                  </span>
                  {person.notes && null}
                </div>
              </td>
              <td>
                {person.customers && person.customers.length > 0 ? (
                  <div className="d-flex flex-column gap-1">
                    {person.customers.map(cp => (
                      <div key={cp.id} className="d-flex flex-column">
                        <span className="text-primary">
                          <span title={cp.customerTitle} style={{ wordBreak: 'break-word' }}>
                            {cp.customerTitle}
                          </span>
                        </span>
                        {customerGroupMap[cp.customerId] && (
                          <small className="text-muted">
                            {customerGroupMap[cp.customerId]}
                          </small>
                        )}
                      </div>
                    ))}
                  </div>
                ) : (
                  <span className="text-muted">No customer</span>
                )}
              </td>
              <td>
                {person.emails.length > 0 ? (
                  <div className="d-flex flex-column">
                    <span>{person.emails[0].address}</span>
                    {person.emails.length > 1 && (
                      <small className="text-muted">+{person.emails.length - 1} more</small>
                    )}
                  </div>
                ) : (
                  <span className="text-muted">No email</span>
                )}
              </td>
              <td>
                {person.phones.length > 0 ? (
                  <div className="d-flex flex-column">
                    <span>{person.phones[0].number}</span>
                    {person.phones.length > 1 && (
                      <small className="text-muted">+{person.phones.length - 1} more</small>
                    )}
                  </div>
                ) : (
                  <span className="text-muted">No phone</span>
                )}
              </td>
              <td>
                <div className="btn-group btn-group-sm action-group" role="group">
                  <button
                    type="button"
                    className="btn action-btn action-view"
                    onClick={() => onViewDetails(person.id)}
                    title="View Details"
                  >
                    <i className="fa-solid fa-eye" aria-hidden="true" />
                  </button>
                  <button
                    type="button"
                    className="btn action-btn action-edit"
                    onClick={() => onEdit(person)}
                    title="Edit"
                  >
                    <i className="fa-solid fa-pen" aria-hidden="true" />
                  </button>
                  <button
                    type="button"
                    className="btn action-btn action-delete"
                    onClick={() => onDelete(person.id)}
                    title="Delete"
                  >
                    <i className="fa-solid fa-trash" aria-hidden="true" />
                  </button>
                </div>
              </td>
            </tr>
          ))}
        </tbody>
        </table>
      </div>
      {persons.length === 0 && (
        <div className="text-center p-4">
          <div className="text-muted">
            <div style={{ fontSize: '2rem', marginBottom: '1rem' }}>
              <i className="fa-solid fa-user" aria-hidden="true"></i>
            </div>
            <h6>No persons found</h6>
            <p>Start by adding a new person to your database.</p>
          </div>
        </div>
      )}
    </div>
  );
};

export default PersonsTable;
